<template>
  <div :class="{ 'has-error': hasErrorMessages }">
    <canvas ref="canvas" class="signature-pad" :height="props.height" :width="props.width" />
    <div v-if="!hasErrorMessages && signature.length">
      <vs-button
        remove-invisible-border
        type="tertiary"
        destructive
        small
        icon="clear"
        @click="clear"
      />
      <vs-button remove-invisible-border type="tertiary" small icon="undo" @click="undo" />
    </div>
    <div v-for="message in errorMessages" :key="message" class="error-message">
      {{ message }}
    </div>
  </div>
</template>

<script setup lang="ts">
import SignaturePad, { PointGroup } from 'signature_pad'
import { onMounted, ref } from 'vue'
import { useFormField } from '@/components/vision/next/forms/useFormAdapter'

const props = withDefaults(
  defineProps<{
    height?: number
    width?: number
    required?: boolean
  }>(),
  {
    height: 200,
    width: 435,
    isRequired: false,
  },
)

let pad: SignaturePad
const canvas = ref<HTMLCanvasElement | null>(null)
const signature = ref<PointGroup[]>([])

const {
  allErrorMessages: errorMessages,
  hasErrorMessages,
  runValidation,
} = useFormField({
  value: signature,
  required: props.required,
})

const emit = defineEmits<{
  (event: 'input', signature: File)
  (event: 'clear')
}>()

onMounted(() => {
  if (!canvas.value) return

  pad = new SignaturePad(canvas.value)
  pad.addEventListener('endStroke', () => {
    signature.value = pad.toData()
    runValidation()
    exportSignature()
  })
})

function exportSignature() {
  canvas.value?.toBlob((blob) => {
    if (!blob) return
    const file = new File([blob], 'signature.png', { type: 'image/png' })
    emit('input', file)
  })
}

function clear() {
  pad.clear()
  signature.value = pad.toData()
  runValidation()
  emit('clear')
}

function undo() {
  const data = pad.toData()

  if (data.length < 2) {
    clear()
  } else {
    data.pop()
    signature.value = pad.toData()
    pad.fromData(data)
    exportSignature()
  }
}
</script>

<style scoped>
.signature-pad {
  border: 1px solid var(--color-grey--lighter);
  border-radius: 24px;
}

.has-error .signature-pad {
  border-color: var(--color-red);
}

.error-message {
  color: var(--color-red);
  margin-bottom: var(--space-small);
  padding: 0 var(--space-base);
}
</style>
